import { useTranslation } from 'react-i18next';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui';
import { Badge } from '@/components/ui/badge';
import { TransactionStatusBadge } from './status-badge';
import { formatDate, formatCents } from '@/lib/utils';
import { MerchantCell } from '@/components/shared/merchant-cell';
import { ServiceCell } from '@/components/shared/service-cell';
import type { SortState } from '@/hooks/use-sort-state';
import type { AdminTransactionSummary } from '@/types/admin-transaction';

/** Sortierbare Spalten der Transaction-Tabelle */
export type TransactionSortColumn = 'createdAt' | 'status' | 'grossAmount';

interface TransactionTableProps {
  transactions: AdminTransactionSummary[];
  onRowClick: (transactionId: string, merchantId?: string) => void;
  sort?: SortState<TransactionSortColumn>;
  onToggleSort?: (column: TransactionSortColumn) => void;
  /** Spec 0220: Merchant-Spalte nur fuer Platform-Rollen (Default: sichtbar). */
  showMerchant?: boolean;
}

export function TransactionTable({
  transactions,
  onRowClick,
  sort,
  onToggleSort,
  showMerchant = true,
}: TransactionTableProps) {
  const { t } = useTranslation('transactions');

  if (transactions.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        {t('list.empty')}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('list.columns.transactionId')}</TableHead>
          {showMerchant && <TableHead>{t('list.columns.merchant')}</TableHead>}
          <TableHead>{t('list.columns.service')}</TableHead>
          <TableHead>{t('list.columns.customerReference')}</TableHead>
          <SortableHead
            label={t('list.columns.status')}
            column="status"
            sort={sort}
            onToggle={onToggleSort}
          />
          <TableHead>{t('list.columns.flow')}</TableHead>
          <TableHead>{t('list.columns.operator')}</TableHead>
          <TableHead>{t('list.columns.mode')}</TableHead>
          <SortableHead
            label={t('list.columns.amount')}
            column="grossAmount"
            sort={sort}
            onToggle={onToggleSort}
          />
          <SortableHead
            label={t('list.columns.created')}
            column="createdAt"
            sort={sort}
            onToggle={onToggleSort}
          />
        </TableRow>
      </TableHeader>
      <TableBody>
        {transactions.map((tx) => (
          <TableRow
            key={tx.transactionId}
            className="cursor-pointer touch-manipulation hover:bg-muted/50 coarse:h-14"
            onClick={() => onRowClick(tx.transactionId, tx.merchantId)}
          >
            <TableCell className="font-mono text-sm">
              <div>{tx.transactionId}</div>
              {/* Spec 475: IQ-id as muted second line — sparse, absent before the
                  first gateway call (v2.17: field missing, not null). */}
              {tx.internetqTransactionId && (
                <div
                  className="max-w-[200px] truncate text-xs text-muted-foreground"
                  title={tx.internetqTransactionId}
                >
                  {tx.internetqTransactionId}
                </div>
              )}
            </TableCell>
            {showMerchant && <TableCell><MerchantCell merchantId={tx.merchantId} /></TableCell>}
            <TableCell><ServiceCell serviceId={tx.serviceId} /></TableCell>
            {/* Spec 475: merchant payment reference (sparse → em dash) */}
            <TableCell
              className="max-w-[160px] truncate text-sm"
              title={tx.customerReference ?? undefined}
            >
              {tx.customerReference ?? '—'}
            </TableCell>
            <TableCell>
              <TransactionStatusBadge status={tx.status} />
            </TableCell>
            <TableCell>{tx.flowType}</TableCell>
            {/* Spec 409: Operator-Spalte. Leer, wenn der Operator (noch) nicht feststeht —
                die Facade liefert OperatorID optional je Zeile (Spec/Plan: „rendert die Zelle leer"). */}
            <TableCell className="font-mono text-sm">{tx.operatorId ?? ''}</TableCell>
            <TableCell>
              <Badge variant={(tx.gatewayMode ?? 'live') === 'live' ? 'success' : 'warning'}>
                {(tx.gatewayMode ?? 'live') === 'live' ? 'LIVE' : 'MOCK'}
              </Badge>
            </TableCell>
            <TableCell>
              {tx.grossAmount != null ? formatCents(tx.grossAmount) : '-'}
            </TableCell>
            <TableCell>{formatDate(tx.createdAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Sortierbare Spaltenkoepfe mit Pfeil-Indikator */
function SortableHead<T extends string>({
  label,
  column,
  sort,
  onToggle,
}: {
  label: string;
  column: T;
  sort?: SortState<T>;
  onToggle?: (column: T) => void;
}) {
  const isActive = sort?.column === column;

  // Spec 514: aria-sort liegt auf dem <th>, das interaktive Element ist ein die Zelle
  // voll ausfuellender <button> (Tastatur + Touch); Toggle-Reihenfolge unveraendert.
  return (
    <TableHead
      aria-sort={isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
      className="p-0"
    >
      <button
        type="button"
        onClick={() => onToggle?.(column)}
        className="flex h-10 w-full cursor-pointer touch-manipulation select-none items-center gap-1 px-2 hover:bg-muted/50 coarse:min-h-11"
      >
        {label}
        {isActive && (
          sort.direction === 'asc'
            ? <ChevronUpIcon className="h-3.5 w-3.5" />
            : <ChevronDownIcon className="h-3.5 w-3.5" />
        )}
      </button>
    </TableHead>
  );
}
